import React, { useEffect, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Box from "@mui/material/Box";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

const EventDateSelector = ({
  instances,
  onInstanceChange,
  initialSelectedIndex = 0,
  selectedInstance, // Add this prop to control the selection from parent
}) => {
  // Use the parent's selected index if provided, otherwise use local state
  const [localSelectedIndex, setLocalSelectedIndex] =
    useState(initialSelectedIndex);
  const selectedIndex =
    selectedInstance !== undefined ? selectedInstance : localSelectedIndex;

  const [dateGroups, setDateGroups] = useState([]);

  // This effect handles initial setup and date grouping
  useEffect(() => {
    if (!instances || instances.length === 0) return;

    try {
      // Find the next upcoming date for default selection
      let nextUpcomingIndex = 0;
      const now = new Date();

      for (let i = 0; i < instances.length; i++) {
        if (instances[i]?.startDate?.seconds) {
          const instanceDate = new Date(instances[i].startDate.seconds * 1000);
          if (instanceDate >= now) {
            nextUpcomingIndex = i;
            break;
          }
        }
      }

      // Only set local state if we're not controlled by parent
      if (selectedInstance === undefined) {
        setLocalSelectedIndex(nextUpcomingIndex);
      }

      // Process date groups
      const groups = {};
      instances.forEach((instance, index) => {
        if (!instance?.startDate?.seconds) return;

        const date = new Date(instance.startDate.seconds * 1000);
        const dateKey = date.toLocaleDateString();

        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: date,
            instances: [],
          };
        }

        groups[dateKey].instances.push({
          instance,
          index,
          time: date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          }),
        });
      });

      const groupsArray = Object.values(groups);
      setDateGroups(groupsArray);

      // Initialize parent only if we're not already being controlled
      if (
        onInstanceChange &&
        selectedInstance === undefined &&
        initialSelectedIndex === 0
      ) {
        onInstanceChange(nextUpcomingIndex);
      }
    } catch (error) {
      console.error("Error processing event dates:", error);
      setDateGroups([]);
    }
  }, [instances, onInstanceChange, initialSelectedIndex, selectedInstance]);

  const handleDateSelect = (instanceIndex) => {
    // Update local state only if we're not controlled by parent
    if (selectedInstance === undefined) {
      setLocalSelectedIndex(instanceIndex);
    }

    // Notify parent component of change
    if (onInstanceChange) {
      onInstanceChange(instanceIndex);
    }
  };

  const formatDateShort = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // If no instances, don't render anything
  if (!dateGroups.length) return null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <CalendarTodayIcon
          fontSize="small"
          sx={{ mr: 1, color: "primary.main" }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          MORE DATES:
        </Typography>
      </Box>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {dateGroups.map((group) =>
          group.instances.map(({ instance, index, time }) => (
            <Grid
              item
              key={instance.id}
              sx={{ width: { xs: "20%", sm: "20%" } }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  height: "100%",
                  textAlign: "center",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    selectedIndex === index ? "primary.main" : "divider",
                  bgcolor:
                    selectedIndex === index
                      ? "primary.light"
                      : "background.paper",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor:
                      selectedIndex === index
                        ? "primary.light"
                        : "action.hover",
                  },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onClick={() => handleDateSelect(index)}
              >
                <Box
                  sx={{
                    bgcolor:
                      selectedIndex === index ? "primary.main" : "grey.100",
                    py: 0.5,
                    color:
                      selectedIndex === index
                        ? "primary.contrastText"
                        : "text.secondary",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {formatDateShort(group.date)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 0.75,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 0.5,
                    }}
                  >
                    <AccessTimeIcon
                      sx={{
                        fontSize: 12,
                        mr: 0.5,
                        color: "text.secondary",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {time}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default EventDateSelector;
